// File: src/pages/MasterTabs.jsx
// ✅ SIMPLE TAB PAGE (like UsersList UI)
// ✅ Each tab ONLY renders a component: <FileName />
// ✅ No fetch, no table, no action bar — just call components
// ✅ Same animated indicator + CSS style like your UsersList

import React, { useEffect, useMemo, useState } from "react";
import { Row, Col } from "reactstrap";
import TargetReport from "./TargetReport";
import SalesOrderReport from "./SalesOrderReport";
import Salesreport from "./SalesReport";
import DeadPartyReport from "./DeadPartyReport";
import TaxReport from "./TaxReport";
import LockPartyReport from "./LockPartyReport";
import BankStatementReport from "./BankStatement";
import ProfitandLossReport from "./ProfitAndLossReport";
import PromotionalActivityReport from "./PromotionalActivityReport";
import UserOpeningClosingBalance from "./UserOpeningClosingBalance";

// ✅ Put your pages/components here (adjust paths as per your project)

/* ---------------- CSS (same style like UsersList) ---------------- */
const CSS_TABS = `
  .ul-shell{
    border-radius: 18px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    box-shadow: 0 10px 24px rgba(0,0,0,0.06);
    overflow: hidden;
  }

  .ul-tabsbar{
    width: 100%;
    background: #ffffff;
    border-bottom: 1px solid #eef2f7;
    padding: 10px 12px;
  }

  .ul-tabswrap{
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
  }

  .ul-tabs{
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: nowrap;
    gap: 0;
    position: relative;
    border-radius: 14px;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    overflow: hidden;
  }

  .ul-tab{
    flex: 1 1 0;
    text-align: center;
    border: none;
    background: transparent;
    padding: 10px 12px;
    font-weight: 900;
    font-size: 14px;
    color: #0f172a;
    cursor: pointer;
    position: relative;
    user-select: none;
    transition: color .2s ease, transform .15s ease;
  }

  .ul-tab:hover{ transform: translateY(-1px); }

  .ul-tab.active{ color: #ffffff; }

  .ul-indicator{
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    border-radius: 14px;
    background: linear-gradient(90deg, #2563eb, #1d4ed8);
    transition: transform .28s cubic-bezier(.2,.8,.2,1);
    box-shadow: 0 10px 18px rgba(37,99,235,0.22);
  }

  @keyframes ulPop {
    0% { transform: scale(.96); }
    100% { transform: scale(1); }
  }
  .ul-tab.active{ animation: ulPop .18s ease; }
`;

const injectOnce = () => {
  if (document.getElementById("mastertabs-inline-css")) return;
  const style = document.createElement("style");
  style.id = "mastertabs-inline-css";
  style.innerHTML = CSS_TABS;
  document.head.appendChild(style);
};

/* ---------------- Tabs ----------------
   ✅ Put exact tabs you want here and just map to component
*/
const TABS = [
  {
    key: "bankStatement",
    label: "Bank Statement",
    render: () => <BankStatementReport />,
  },
  {
    key: "lockParty",
    label: "Lock Party",
    render: () => <LockPartyReport />,
  },
  {
    key: "openingClosing",
    label: "Opening & Closing Balances",
    render: () => <UserOpeningClosingBalance />,
  },
];

export default function FinanceReportFlow() {
  const [activeTab, setActiveTab] = useState(TABS[0]?.key || "users");

  useEffect(() => injectOnce(), []);

  const activeIndex = useMemo(
    () =>
      Math.max(
        0,
        TABS.findIndex((t) => t.key === activeTab),
      ),
    [activeTab],
  );

  const tabWidthPct = 100 / (TABS.length || 1);

  const ActiveComponent = useMemo(() => {
    const t = TABS.find((x) => x.key === activeTab);
    return t?.render ? t.render : () => null;
  }, [activeTab]);

  return (
    <Row style={{ marginBottom: 10 }}>
      <Col sm="12">
        <div className="ul-shell">
          {/* TAB BAR */}
          <div className="ul-tabsbar">
            <div className="ul-tabswrap">
              <div className="ul-tabs">
                <div
                  className="ul-indicator"
                  style={{
                    width: `${tabWidthPct}%`,
                    transform: `translateX(${activeIndex * 100}%)`,
                  }}
                />
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className={`ul-tab ${activeTab === t.key ? "active" : ""}`}
                    onClick={() => setActiveTab(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* BODY: ONLY CALL COMPONENT */}
          <div style={{ padding: 12 }}>
            <ActiveComponent />
          </div>
        </div>
      </Col>
    </Row>
  );
}
